import { useState, type ReactNode } from 'react';
import { AppleLogo, FacebookLogo, GoogleLogo, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { authClient } from '../../lib/api';
import { mapAuthError } from '@mybusinessmate/api-client';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { useToast } from '../ui/toast';
import { Icon } from '../Icon';

export interface SocialAuthButtonsProps {
  /** Where the OAuth round-trip lands, as a clean path on the current host (e.g. `/audit`). */
  callbackPath: string;
  /** The "…with email" button, rendered last (it navigates, so the caller owns it). */
  children: ReactNode;
}

type Provider = 'google' | 'facebook';

const LABEL = { google: 'Google', facebook: 'Facebook' } satisfies Record<Provider, string>;

/**
 * Figma social chooser: Apple (dark, primary), Google, Facebook, then email. Apple has no backend
 * provider yet, so it is rendered per the design but only tells the user it is coming.
 */
export function SocialAuthButtons({ callbackPath, children }: SocialAuthButtonsProps) {
  const toast = useToast();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<Provider>();

  const signInWith = async (provider: Provider) => {
    setError(undefined);
    setPending(provider);
    const { error: authError } = await authClient.signIn.social({
      provider,
      callbackURL: `${window.location.origin}${callbackPath}`,
    });
    // On success the browser is already navigating to the provider; we only get here on failure.
    if (authError) {
      const mapped = mapAuthError(authError);
      setError(
        mapped?.code === 'PROVIDER_NOT_FOUND'
          ? `${LABEL[provider]} sign-in isn't available right now. Use email instead.`
          : (mapped?.message ?? 'Something went wrong. Please try again.'),
      );
      setPending(undefined);
    }
  };

  const social = (
    icon: PhosphorIcon,
    label: string,
    onClick: () => void,
    variant: 'primary' | 'secondary',
    loading = false,
  ) => (
    <Button variant={variant} fullWidth onClick={onClick} loading={loading}>
      <Icon icon={icon} size="xl" />
      {label}
    </Button>
  );

  return (
    <div className="flex flex-col gap-(--mbm-space-3)">
      {error ? <Banner tone="error">{error}</Banner> : null}
      {social(
        AppleLogo,
        'Continue with Apple',
        () => toast({ title: 'Apple sign-in is coming soon', tone: 'info' }),
        'primary',
      )}
      {social(GoogleLogo, 'Continue with Google', () => void signInWith('google'), 'secondary', pending === 'google')}
      {social(
        FacebookLogo,
        'Continue with Facebook',
        () => void signInWith('facebook'),
        'secondary',
        pending === 'facebook',
      )}
      {children}
    </div>
  );
}
